import { isDateOutOfRange, getDateRange, getDateRangeHint } from '../utils/validation.js';
import { normalizeAge, retirementDateFromAge, monthYear } from '../store/planRetirement.js';

// The same info icon the PQ's tables use, so the two forms look like one app.
// tabIndex makes it reachable by keyboard; title carries the tooltip and
// aria-label the same text for screen readers.
function InfoIcon({ text }) {
  return (
    <span
      className="plan-field-info"
      tabIndex={0}
      title={text}
      aria-label={text}
    >
      i
    </span>
  );
}

// `missing` marks a field Submit found empty. Naming them in the banner alone
// left advisors hunting across four sections by trial and error (Rachel Fuss,
// 2026-08-10), so the field itself says so.
// `disabled` greys a question the household's answers make inapplicable — see
// `onlyWhen` in planSections. The control is still rendered and still labelled,
// so the advisor can see the question exists and why it is unavailable, rather
// than having it vanish from under them.
// `ageValue` / `onAgeChange` / `dob` are only passed for a `retirement` field,
// which takes its answer as an age or a date.
export default function PlanField({ field, value, onChange, missing = false, disabled = false, ageValue, dob, onAgeChange }) {
  if (field.type === 'checkbox') {
    return (
      <div className={'plan-field-row plan-field-row--check' + (disabled ? ' is-disabled' : '')}>
        <input
          type="checkbox"
          id={`plan-${field.key}`}
          checked={value === true || value === 'true'}
          disabled={disabled}
          onChange={e => onChange(e.target.checked)}
        />
        <label htmlFor={`plan-${field.key}`}>{field.label}</label>
        {field.labelInfo && <InfoIcon text={field.labelInfo} />}
      </div>
    );
  }

  return (
    <div className={'plan-field-row' + (missing ? ' is-missing' : '') + (disabled ? ' is-disabled' : '')}>
      <label htmlFor={field.ageKey ? `plan-${field.ageKey}` : `plan-${field.key}`} className="plan-field-label">
        {field.label}
        {field.labelInfo && <InfoIcon text={field.labelInfo} />}
        {missing && <span className="plan-field-req" aria-hidden="true"> Required</span>}
      </label>
      {renderControl(field, value, onChange, missing, disabled, { ageValue, dob, onAgeChange })}
    </div>
  );
}

function renderControl(field, value, onChange, missing, disabled, extra = {}) {
  const v = value ?? '';
  const id = `plan-${field.key}`;
  const cls = 'plan-field-input' + (missing ? ' is-missing' : '');

  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    const toggle = opt => {
      const next = selected.includes(opt)
        ? selected.filter(x => x !== opt)
        : [...selected, opt];
      onChange(next);
    };
    return (
      <div className="multiselect-group">
        {(field.options || []).map(opt => (
          <label key={opt} className="multiselect-item">
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              disabled={disabled}
              onChange={() => toggle(opt)}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <select
        id={id}
        className={cls}
        value={v}
        disabled={disabled}
        aria-invalid={missing || undefined}
        onChange={e => onChange(e.target.value)}
      >
        {(field.options || []).map(opt => (
          <option key={opt} value={opt}>{opt || '— Select —'}</option>
        ))}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        id={id}
        className="plan-field-input plan-field-ta"
        rows={3}
        value={v}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  if (field.type === 'retirement') {
    const { ageValue, dob, onAgeChange } = extra;
    const { min, max } = getDateRange(field.dateRange);
    const invalid = isDateOutOfRange(v, min, max);
    const age = ageValue ?? '';
    const cleanAge = normalizeAge(age);
    const built = cleanAge ? retirementDateFromAge(dob, cleanAge) : '';
    // Say what planning will actually receive, because when a date of birth is
    // known the age box is not what gets sent.
    let hint = '';
    if (cleanAge && built) hint = `Sent to planning as ${monthYear(built)}, from the date of birth.`;
    else if (cleanAge) hint = `No date of birth on the questionnaire, so this is sent as "Age ${cleanAge}".`;
    return (
      <div className="plan-retire-wrap">
        <div className="plan-retire">
          <input
            type="number"
            id={`plan-${field.ageKey}`}
            className={`${cls} plan-retire-age`}
            min={1}
            max={120}
            step="any"
            inputMode="decimal"
            placeholder="Age"
            aria-label={`${field.label}: age`}
            value={age}
            disabled={disabled}
            aria-invalid={missing || undefined}
            onChange={e => onAgeChange?.(e.target.value)}
          />
          <span className="plan-retire-or" aria-hidden="true">or</span>
          <input
            type="date"
            id={id}
            className={invalid ? `${cls} plan-retire-date date-invalid` : `${cls} plan-retire-date`}
            min={min}
            max={max}
            aria-label={`${field.label}: date`}
            value={v}
            disabled={disabled}
            onChange={e => onChange(e.target.value)}
            aria-invalid={invalid || missing || undefined}
          />
        </div>
        {invalid && <span className="field-hint-error">{getDateRangeHint(field.dateRange)}</span>}
        {hint && <span className="plan-field-hint">{hint}</span>}
      </div>
    );
  }

  if (field.type === 'date') {
    const { min, max } = getDateRange(field.dateRange);
    const invalid = isDateOutOfRange(v, min, max);
    return (
      <>
        <input
          type="date"
          id={id}
          className={invalid ? `${cls} date-invalid` : cls}
          min={min}
          max={max}
          value={v}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          aria-invalid={invalid || missing || undefined}
        />
        {invalid && <span className="field-hint-error">{getDateRangeHint(field.dateRange)}</span>}
      </>
    );
  }

  return (
    <input
      type="text"
      id={id}
      className={cls}
      value={v}
      disabled={disabled}
      aria-invalid={missing || undefined}
      onChange={e => onChange(e.target.value)}
    />
  );
}
